<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎼 Sequenced Note Reading Question Demo</h1>

        <div class="layout">
            <!-- Left Panel: Sequence & Presets -->
            <div class="left-panel bg-surface">
                <h2 class="text-h5 text-secondary">Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Note Sequence</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="sequence-input">Sequence (comma-separated):</label>
                            <textarea
                                id="sequence-input"
                                v-model="sequenceInput"
                                placeholder="e.g., 60, 62, 64, 65, 67 (MIDI numbers with optional #, b, n decorators)"
                                class="text-area"
                                rows="3"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Quick Presets</h3>
                    <div class="property-group">
                        <button
                            v-for="preset in presets"
                            :key="preset.name"
                            @click="loadPreset(preset)"
                            class="preset-btn"
                        >
                            {{ preset.name }}
                        </button>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Melody Label</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="label-input">Optional label:</label>
                            <input
                                id="label-input"
                                v-model="label"
                                type="text"
                                placeholder="e.g., C Major Scale, Folk Melody"
                                class="text-input"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Key Signature</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="keysig-input">Optional key signature:</label>
                            <input
                                id="keysig-input"
                                v-model="keySig"
                                type="text"
                                placeholder="e.g., C major, G major, D minor"
                                class="text-input"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Center Panel: Live Question -->
            <div class="center-panel bg-surface">
                <h2 class="text-h5 text-secondary">Live Question</h2>
                <div class="preview-box">
                    <SequencedNoteReadingQuestionView
                        v-if="sequenceStr"
                        :key="questionKey"
                        :data="questionData"
                        @emit-response="handleResponse"
                        @note-matched="handleNoteMatched"
                        @wrong-note="handleWrongNote"
                        @note-ended="handleNoteEnded"
                        @sequence-complete="handleSequenceComplete"
                        @sequence-failed="handleSequenceFailed"
                    />
                    <div v-else class="empty-state">
                        <p>Enter a sequence and click "Apply Configuration" to begin</p>
                    </div>
                </div>

                <!-- Event Log -->
                <v-card class="event-log bg-surface-light">
                    <v-card-title class="text-h6">
                        📡 Event Log
                        <button @click="eventLog = []" class="clear-log-btn">Clear</button>
                    </v-card-title>
                    <v-card-text>
                        <div class="event-display">
                            <div v-if="eventLog.length === 0" class="empty-log">
                                No events yet. Start playing to see events...
                            </div>
                            <div
                                v-for="(event, idx) in eventLog"
                                :key="idx"
                                class="event-row"
                                :class="'event-' + event.type"
                            >
                                <span class="event-time">{{ event.timestamp }}</span>
                                <span class="event-type">{{ event.type }}</span>
                                <span class="event-detail">{{ event.detail }}</span>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Response Monitor -->
                <v-card v-if="lastResponse" class="response-monitor bg-surface-light">
                    <v-card-title class="text-h6">
                        📊 Last Response
                    </v-card-title>
                    <v-card-text>
                        <div class="response-display">
                            <div class="response-row">
                                <strong>Completed:</strong>
                                <span :class="lastResponse.completed ? 'text-success' : 'text-error'">
                                    {{ lastResponse.completed ? '✅ Yes' : '❌ No' }}
                                </span>
                            </div>
                            <div v-if="label" class="response-row">
                                <strong>Label:</strong>
                                {{ label }}
                            </div>
                            <div v-if="keySig" class="response-row">
                                <strong>Key Signature:</strong>
                                {{ keySig }}
                            </div>
                            <div class="response-row">
                                <strong>Sequence:</strong>
                                {{ lastResponse.sequence.join(', ') }}
                            </div>
                            <div class="response-row">
                                <strong>Wrong Attempts:</strong>
                                {{ lastResponse.wrongAttempts }}
                            </div>
                            <div class="response-row">
                                <strong>Time Elapsed:</strong>
                                {{ (lastResponse.timeElapsed / 1000).toFixed(2) }}s
                            </div>
                            <div class="response-row">
                                <strong>Score:</strong>
                                {{ (lastResponse.score * 100).toFixed(0) }}%
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>

            <!-- Right Panel: Options & Actions -->
            <div class="right-panel bg-surface">
                <button @click="applyConfiguration" class="apply-btn full-width">
                    ✓ Apply Configuration
                </button>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Pitch Tolerance</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="tolerance-slider">
                                Tolerance: {{ toleranceCents }} cents
                            </label>
                            <input
                                id="tolerance-slider"
                                type="range"
                                v-model.number="toleranceCents"
                                min="10"
                                max="45"
                                step="5"
                            />
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Display Options</h3>
                    <div class="property-group">
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="showFingerings" />
                            Show Fingering Diagrams
                        </label>
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="showPitchVisualizer" />
                            Show Pitch Visualizer
                        </label>
                    </div>
                    <div class="property-group" v-if="showPitchVisualizer">
                        <label for="visualizer-variant">Visualizer Style:</label>
                        <select id="visualizer-variant" v-model="pitchVisualizerVariant" class="select-input">
                            <option value="default">Default (full gradient)</option>
                            <option value="minimal">Minimal (discrete)</option>
                        </select>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Actions</h3>
                    <div class="property-group">
                        <button @click="resetQuestion" class="reset-btn">
                            🔄 Reset Question
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Instructions -->
        <v-card class="instructions-section">
            <v-card-title class="text-h5 text-secondary">
                How to Use
            </v-card-title>
            <v-card-text>
                <ol class="usage-list">
                    <li>
                        <strong>Enter a note sequence</strong> using comma-separated MIDI numbers (e.g., "60, 62, 64" for C4, D4, E4) or select a preset
                    </li>
                    <li>
                        <strong>MIDI Format:</strong> Use numbers 60-96 (C4 to C7). Optional decorators: # (force sharp), b (force flat), n (natural note only). Examples: "61#" (C#), "61b" (Db), "60n" (C-natural)
                    </li>
                    <li>
                        <strong>Configure tolerance and options</strong> to adjust difficulty and matching behavior
                    </li>
                    <li>
                        <strong>Click "Apply Configuration"</strong> to load the question
                    </li>
                    <li>
                        <strong>Play each note in sequence</strong> - the current target note is highlighted
                    </li>
                    <li>
                        <strong>Visual feedback:</strong> Correct notes turn gray, wrong notes flash red with overlay
                    </li>
                    <li>
                        <strong>Complete the sequence</strong> to submit an answer and see your score
                    </li>
                    <li>
                        Check the "Last Response" panel to see the evaluation results
                    </li>
                </ol>
            </v-card-text>
        </v-card>

        <!-- Scoring Info -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                Performance Scoring
            </v-card-title>
            <v-card-text>
                <p>The question evaluates your performance based on:</p>
                <ul class="api-list">
                    <li><strong>Completion:</strong> 70% base score for completing the sequence</li>
                    <li><strong>Accuracy Bonus:</strong>
                        <ul>
                            <li>+30% (100% total) for perfect run (0 wrong attempts)</li>
                            <li>+20% (90% total) for good run (< 50% wrong ratio)</li>
                            <li>+10% (80% total) for okay run (< 100% wrong ratio)</li>
                            <li>+0% (70% total) for > 1 wrong attempt per note</li>
                        </ul>
                    </li>
                </ul>
                <p class="mt-4"><strong>Example Scores:</strong></p>
                <ul class="api-list">
                    <li>5-note sequence, 0 wrong: 100%</li>
                    <li>5-note sequence, 2 wrong: 90%</li>
                    <li>5-note sequence, 4 wrong: 80%</li>
                    <li>5-note sequence, 6+ wrong: 70%</li>
                </ul>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import SequencedNoteReadingQuestionView from '../questions/SequencedNoteReadingQuestionView.vue';
import { SequencedNoteReadingQuestion, type SequencedNoteReadingAnswer } from '../questions/SequencedNoteReadingQuestion';

interface Preset {
    name: string;
    sequence: string;
    keySig?: string;
    label?: string;
}

const sequenceInput = ref('60, 62, 64, 65, 67');
const toleranceCents = ref(40);
const showFingerings = ref(true);
const showPitchVisualizer = ref(false);
const pitchVisualizerVariant = ref<'default' | 'minimal'>('minimal');
const label = ref('');
const keySig = ref('');
const questionKey = ref(0);

const sequenceStr = ref<string>('');

const presets: Preset[] = [
    {
        name: 'C Major Scale',
        sequence: '60, 62, 64, 65, 67, 69, 71, 72',
        keySig: 'C',
        label: 'C Major Scale'
    },
    {
        name: 'G Major Scale',
        sequence: '67, 69, 71, 72, 74, 76, 78#, 79',
        keySig: 'G',
        label: 'G Major Scale'
    },
    {
        name: 'F Major Scale',
        sequence: '65, 67, 69, 70b, 72, 74, 76, 77',
        keySig: 'F',
        label: 'F Major Scale'
    },
    {
        name: 'C Major Arpeggio',
        sequence: '60, 64, 67, 72',
        keySig: 'C',
        label: 'C Major Arpeggio'
    },
    {
        name: 'Simple Pattern',
        sequence: '60, 62, 60, 62, 60',
        keySig: 'C',
        label: 'Simple Up-Down Pattern'
    },
    {
        name: 'Melodic Pattern',
        sequence: '60, 62, 64, 65, 67, 65, 64, 62, 60',
        keySig: 'C',
        label: 'Ascending and Descending'
    },
    {
        name: 'Repeated Notes',
        sequence: '60, 60, 60, 62, 62, 62, 64, 64, 64',
        keySig: 'C',
        label: 'Repeated Note Exercise'
    },
    {
        name: 'Short Test',
        sequence: '60, 62, 64',
        keySig: 'C',
        label: 'Short Test'
    },
    {
        name: 'Decorator Test: Explicit Accidentals',
        sequence: '60, 61#, 61b, 62, 63#, 63b',
        keySig: 'C',
        label: 'Decorator Test'
    },
    {
        name: 'D Major Scale (with F# and C#)',
        sequence: '62, 64, 66#, 67, 69, 71, 73#, 74',
        keySig: 'D',
        label: 'D Major Scale'
    },
    {
        name: 'Bb Major Scale (with Bb and Eb)',
        sequence: '70b, 72, 74, 75b, 77, 79, 81, 82',
        keySig: 'Bb',
        label: 'Bb Major Scale'
    },
    {
        name: 'Chromatic Run (C to G)',
        sequence: '60, 61#, 62, 63#, 64, 65, 66#, 67',
        keySig: 'C',
        label: 'Chromatic (sharps)'
    },
    {
        name: 'Chromatic Run (C to G, flats)',
        sequence: '60, 61b, 62, 63b, 64, 65, 66b, 67',
        keySig: 'C',
        label: 'Chromatic (flats)'
    },
];

const lastResponse = ref<(SequencedNoteReadingAnswer & { score: number }) | null>(null);

interface EventLogEntry {
    timestamp: string;
    type: 'note-matched' | 'wrong-note' | 'note-ended' | 'sequence-complete' | 'sequence-failed' | 'emit-response';
    detail: string;
}

const eventLog = ref<EventLogEntry[]>([]);

const questionData = computed(() => {
    if (!sequenceStr.value) return [];

    return [
        {
            sequence: sequenceStr.value,
            toleranceCents: toleranceCents.value,
            showFingerings: showFingerings.value ? 'true' : 'false',
            showPitchVisualizer: showPitchVisualizer.value ? 'true' : 'false',
            pitchVisualizerVariant: pitchVisualizerVariant.value,
            label: label.value || undefined,
            keySig: keySig.value || undefined,
        },
    ];
});

function applyConfiguration() {
    const parsed = sequenceInput.value
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

    if (parsed.length < 2) {
        alert('Please enter at least 2 notes');
        return;
    }

    // Store as comma-separated string (what the question expects)
    sequenceStr.value = sequenceInput.value.trim();
    questionKey.value++;
    lastResponse.value = null;
    eventLog.value = [];
}

function loadPreset(preset: Preset) {
    sequenceInput.value = preset.sequence;
    if (preset.keySig !== undefined) {
        keySig.value = preset.keySig;
    }
    if (preset.label !== undefined) {
        label.value = preset.label;
    }
    applyConfiguration();
}

function handleResponse(response: any) {
    console.log('Question response:', response);

    // The evaluate method returns a number (score from 0-1)
    // Note: SequencedNoteReadingQuestion extends Question but doesn't override constructor
    // so it likely doesn't need ViewData for evaluation
    const score = SequencedNoteReadingQuestion.prototype.evaluate.call(
        { },  // minimal context object
        response.answer
    );

    lastResponse.value = {
        ...response.answer,
        score,
    };

    // Log the final response emission (indicates note was released and answer submitted)
    logEvent('emit-response', `Answer submitted: ${response.answer.completed ? 'Success' : 'Failed'} (score: ${(score * 100).toFixed(0)}%)`);
}

function resetQuestion() {
    questionKey.value++;
    lastResponse.value = null;
    eventLog.value = [];
}

function logEvent(type: EventLogEntry['type'], detail: string) {
    const now = new Date();
    const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;

    eventLog.value.push({
        timestamp,
        type,
        detail,
    });
}

function handleNoteMatched(note: string, index: number) {
    logEvent('note-matched', `Note ${index + 1}: ${note}`);
}

function handleWrongNote(detected: string, expected: string) {
    logEvent('wrong-note', `Played ${detected}, expected ${expected}`);
}

function handleNoteEnded(note: string) {
    logEvent('note-ended', `Released: ${note}`);
}

function handleSequenceComplete(totalAttempts: number) {
    logEvent('sequence-complete', `Sequence complete! (${totalAttempts} wrong attempts) - Final answer submitted`);
}

function handleSequenceFailed(totalAttempts: number) {
    logEvent('sequence-failed', `Failed after ${totalAttempts} wrong attempts`);
}
</script>

<style scoped>
.debug-container {
    width: 100%;
    margin: 0;
    padding: 20px;
    display: flex;
    flex-direction: column;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 20px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 1fr 1.5fr 1fr;
    gap: 20px;
    margin-bottom: 40px;
    height: calc(100vh - 200px);
}

.left-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
}

.center-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.right-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.hint {
    display: block;
    font-size: 12px;
    color: #666;
    margin-top: 4px;
}

.text-area {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    font-family: monospace;
    resize: vertical;
}

.text-area:focus {
    outline: none;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.2s;
}

.checkbox-label:hover {
    background: rgba(0, 0, 0, 0.03);
}

.checkbox-label input[type="checkbox"] {
    cursor: pointer;
}

.select-input {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: white;
    cursor: pointer;
}

.select-input:focus {
    outline: none;
    border-color: rgba(0, 0, 0, 0.4);
}

.text-input {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
}

.text-input:focus {
    outline: none;
    border-color: rgba(0, 0, 0, 0.4);
}

.preset-btn {
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s;
    text-align: left;
    font-weight: 500;
    width: 100%;
}

.preset-btn:hover {
    opacity: 0.8;
}

.apply-btn,
.reset-btn {
    width: 100%;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.apply-btn {
    background: #28a745;
    margin-bottom: 20px;
}

.apply-btn:hover {
    background: #218838;
}

.apply-btn.full-width {
    width: 100%;
}

.reset-btn {
    background: #007bff;
}

.reset-btn:hover {
    background: #0056b3;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 20px;
    border-radius: 6px;
    flex: 1;
}

.empty-state {
    text-align: center;
    color: #666;
    font-size: 16px;
}

.event-log {
    margin-top: 20px;
    flex-shrink: 0;
}

.event-log .v-card-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.clear-log-btn {
    padding: 4px 12px;
    font-size: 12px;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
}

.clear-log-btn:hover {
    background: #c82333;
}

.event-display {
    font-family: monospace;
    font-size: 12px;
    max-height: 300px;
    overflow-y: auto;
}

.empty-log {
    text-align: center;
    color: #999;
    padding: 20px;
    font-style: italic;
}

.event-row {
    padding: 6px 8px;
    margin-bottom: 2px;
    border-radius: 3px;
    display: flex;
    gap: 10px;
    align-items: center;
    border-left: 3px solid transparent;
}

.event-time {
    color: #666;
    font-size: 11px;
    min-width: 90px;
}

.event-type {
    font-weight: 600;
    min-width: 140px;
    text-transform: uppercase;
    font-size: 11px;
}

.event-detail {
    flex: 1;
}

.event-note-matched {
    background: rgba(40, 167, 69, 0.1);
    border-left-color: #28a745;
}

.event-note-matched .event-type {
    color: #28a745;
}

.event-wrong-note {
    background: rgba(220, 53, 69, 0.1);
    border-left-color: #dc3545;
}

.event-wrong-note .event-type {
    color: #dc3545;
}

.event-note-ended {
    background: rgba(108, 117, 125, 0.1);
    border-left-color: #6c757d;
}

.event-note-ended .event-type {
    color: #6c757d;
}

.event-sequence-complete {
    background: rgba(40, 167, 69, 0.15);
    border-left-color: #28a745;
    font-weight: 600;
}

.event-sequence-complete .event-type {
    color: #28a745;
}

.event-sequence-failed {
    background: rgba(220, 53, 69, 0.2);
    border-left-color: #dc3545;
}

.event-sequence-failed .event-type {
    color: #dc3545;
}

.event-emit-response {
    background: rgba(0, 123, 255, 0.15);
    border-left-color: #004085;
    font-weight: 600;
}

.event-emit-response .event-type {
    color: #004085;
}

.response-monitor {
    margin-top: 20px;
    flex-shrink: 0;
}

.response-display {
    font-family: monospace;
    font-size: 13px;
}

.response-row {
    padding: 6px 8px;
    margin-bottom: 4px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.02);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.response-row strong {
    min-width: 120px;
    color: #666;
}

.text-success {
    color: #28a745;
    font-weight: 700;
}

.text-error {
    color: #dc3545;
    font-weight: 700;
}

.instructions-section {
    margin: 40px 0;
}

.usage-list {
    line-height: 1.8;
    padding-left: 20px;
}

.usage-list li {
    margin-bottom: 12px;
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
